import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export const WebPushTestButton = () => {
  const { user, session, loading } = useAuth();
  const [sending, setSending] = useState(false);

  if (Capacitor.isNativePlatform()) return null;

  const sendTest = async () => {
    if (loading) {
      toast.error("Checking sign-in status. Try again in a moment.");
      return;
    }

    const { data: latestSessionData, error: sessionError } = await supabase.auth.getSession();
    const accessToken = latestSessionData.session?.access_token ?? session?.access_token;

    if (sessionError || !user || !accessToken) {
      toast.error("Sign in first so your iPhone can be linked to this account.");
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-push", {
        body: {
          mode: "test",
          title: "Airport Buddy test",
          body: "Test push from your laptop. If your iPhone is locked, this should appear on the lock screen.",
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (error) throw error;

      if (data?.sent > 0) {
        toast.success(`Test push sent to ${data.sent} iPhone device(s)`);
      } else {
        toast.error(data?.message ?? "No iPhone devices found. Reopen the iPhone app and try again.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send test push";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Send className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-foreground">Send test push to iPhone</p>
            <p className="mt-1 text-base text-muted-foreground">
              Lock your iPhone first, then send from this browser.
            </p>
            <Button
              type="button"
              onClick={sendTest}
              disabled={sending}
              className="mt-3 min-h-[44px] active:scale-95"
            >
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send test push
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};